import {useEffect, useState} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Developer settings: switches for testing, hidden from normal users.
// Unlocked by tapping the version number on Profile 5 times; the
// "Developer settings" row then stays until it is hidden again from that
// screen. Saved on this device only (AsyncStorage), never sent anywhere.
//
// To add a flag: add it to DEV_FLAGS, add root.DevSettings.flags.<key>.title
// and .description to both language files, and read it with getDevFlag().

const STORAGE_KEY = 'devSettings';

export const DEV_FLAGS = [
  // Receipt upload rules (services/scan-rules.js). The server may still
  // enforce its own checks.
  {key: 'skipScanDistance', default: false},
  {key: 'skipScanDailyLimit', default: false},
];

const defaults = () => ({
  unlocked: false,
  flags: Object.fromEntries(DEV_FLAGS.map(f => [f.key, f.default])),
});

let state = null;
let loading = null;
const listeners = new Set();

const load = () => {
  if (state) {
    return Promise.resolve(state);
  }
  if (!loading) {
    loading = (async () => {
      const base = defaults();
      try {
        const saved = JSON.parse(await AsyncStorage.getItem(STORAGE_KEY));
        state = {
          unlocked: !!saved?.unlocked,
          flags: {...base.flags, ...(saved?.flags || {})},
        };
      } catch (e) {
        state = base;
      }
      return state;
    })();
  }
  return loading;
};

const save = async next => {
  state = next;
  listeners.forEach(fn => fn(state));
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
};

export const getDevSettings = load;

// A flag's value. Always the default while developer settings are locked,
// so a forgotten switch can't change the app for a normal user.
export const getDevFlag = async key => {
  const s = await load();
  const def = DEV_FLAGS.find(f => f.key === key)?.default ?? false;
  return s.unlocked ? s.flags[key] ?? def : def;
};

export const setDevFlag = async (key, value) => {
  const s = await load();
  await save({...s, flags: {...s.flags, [key]: value}});
};

export const setDevUnlocked = async unlocked => {
  const s = await load();
  await save(unlocked ? {...s, unlocked: true} : defaults());
};

export const resetDevFlags = async () => {
  const s = await load();
  await save({...s, flags: defaults().flags});
};

// {unlocked, flags} or null while loading; updates when anything changes.
export const useDevSettings = () => {
  const [value, setValue] = useState(state);
  useEffect(() => {
    let active = true;
    listeners.add(setValue);
    load().then(s => active && setValue(s));
    return () => {
      active = false;
      listeners.delete(setValue);
    };
  }, []);
  return value;
};
